<script>
import model from "@/mixins/model";

const weekdayOptions = [
  { name: "Todos", value: 7 },
  { name: "Dom", value: 0 },
  { name: "Lun", value: 1 },
  { name: "Mar", value: 2 },
  { name: "Mié", value: 3 },
  { name: "Jue", value: 4 },
  { name: "Vie", value: 5 },
  { name: "Sáb", value: 6 },
];

const weekdayFullNames = {
  0: "Domingo",
  1: "Lunes",
  2: "Martes",
  3: "Miércoles",
  4: "Jueves",
  5: "Viernes",
  6: "Sábado",
};

// ["00:00", "00:30", "01:00", "01:30", ... "23:30"]
const timeOptions = [];
const hours = [...Array(24).keys()].map((i) => (i < 10 ? `0${i}` : `${i}`));
const minutes = ["00", "30"];

hours.forEach((hour) => {
  minutes.forEach((minute) => {
    timeOptions.push(`${hour}:${minute}`);
  });
});

// [1, 2, 3, ... 365]
const dayOptions = [...Array(365).keys()].map((i) => i + 1);

const defaultFromDay = 1;
const defaultToDay = 30;

/**
 * @component TaskPeriodization
 * @description Configuración de cuándo se dispara una tarea: modo (Periódico /
 *              Días específicos), rango de días del protocolo, días de la
 *              semana o días puntuales, y horarios. Incluye un panel de
 *              resumen al pie con la programación actual.
 *              Layout v3: label-izquierda + field-derecha, section-title
 *              UPPERCASE, slider/cinta para cantidad de días, day-pills
 *              toggleables.
 */
export default {
  name: "TaskPeriodization",

  mixins: [model],

  data() {
    return {
      timeOptions,
      weekdayOptions,
      dayOptions,
      range: [defaultFromDay, defaultToDay],
    };
  },

  computed: {
    /**
     * @description Cantidad de días del rango seleccionado para mostrar
     *              en el valor numérico arriba del slider.
     * @returns {number}
     */
    rangeDaysCount() {
      return this.range[1] - this.range[0] + 1;
    },

    /**
     * @description Texto auxiliar al lado del valor numérico ("≈ 1 año").
     * @returns {string}
     */
    rangeDaysHint() {
      const count = this.rangeDaysCount;
      if (count >= 365) return "1 año";
      if (count >= 180) return "≈ 6 meses";
      if (count >= 90) return "≈ 3 meses";
      if (count >= 30) return "≈ 1 mes";
      if (count >= 7) return "≈ 1 semana";
      return "";
    },

    /**
     * @description Texto del bullet "Días" en el panel de resumen, modo
     *              periódico. Genera HTML con `<b>` en los nombres seleccionados.
     * @returns {string}
     */
    rangeDaysSummary() {
      const selected = this.weekdayOptions.filter(
        (d) => d.value !== 7 && this.model.periodization.days.includes(d.value)
      );

      if (selected.length === 7) {
        return "<b>Todos los días</b>";
      }

      if (selected.length === 0) {
        return "";
      }

      const fullNames = selected.map(
        (d) => `<b>${weekdayFullNames[d.value]}</b>`
      );

      if (fullNames.length === 1) {
        return `Los días ${fullNames[0]}`;
      }

      const last = fullNames.pop();
      return `Los días ${fullNames.join(", ")} y ${last}`;
    },

    /**
     * @description Texto del bullet "Días" en el panel de resumen, modo
     *              días específicos. Lista los números de día con `<b>`.
     * @returns {string}
     */
    explicitDaysSummary() {
      const days = this.model.periodization.days;
      if (!days.length) return "";

      const sorted = [...days].sort((a, b) => a - b);
      const formatted = sorted.map((d) => `<b>${d}</b>`);

      if (formatted.length === 1) {
        return `En el día ${formatted[0]} del protocolo`;
      }

      const last = formatted.pop();
      return `En los días ${formatted.join(", ")} y ${last} del protocolo`;
    },

    /**
     * @description Texto del bullet "Horarios" en el panel de resumen.
     * @returns {string}
     */
    hoursSummary() {
      const hours = this.model.periodization.hours;
      if (!hours.length) return "";

      const formatted = hours.map((h) => `<b>${h} hs</b>`);
      if (formatted.length === 1) return `En el horario ${formatted[0]}`;

      const last = formatted.pop();
      return `En los horarios ${formatted.join(", ")} y ${last}`;
    },
  },

  created() {
    this.range = [
      this.model.periodization.fromDay || defaultFromDay,
      this.model.periodization.toDay || defaultToDay,
    ];
  },

  watch: {
    "periodization.type"() {
      this.model.periodization.days = [];
    },

    range() {
      this.model.periodization.fromDay = this.range[0];
      this.model.periodization.toDay = this.range[1];
    },
  },

  methods: {
    /**
     * @description Toggle in/out de un día individual de la semana.
     * @param {number} day - Valor del día (0–6)
     */
    toggleDay(day) {
      const index = this.model.periodization.days.findIndex((d) => d === day);
      if (index >= 0) {
        this.model.periodization.days.splice(index, 1);
      } else {
        this.model.periodization.days.push(day);
      }
    },

    /**
     * @description Click en una pill de día. Si es "Todos" (value 7), selecciona
     *              o limpia los 7 días según el estado actual. Si es un día
     *              individual, hace toggle.
     * @param {number} day - Valor del día (0–7, donde 7 = "Todos")
     */
    selectDay(day) {
      if (day !== 7) {
        this.toggleDay(day);
        return;
      }

      const allSelected = this.model.periodization.days.length === 7;
      if (allSelected) {
        this.model.periodization.days = [];
      } else {
        this.model.periodization.days = [0, 1, 2, 3, 4, 5, 6];
      }
    },

    /**
     * @description Indica si una pill de día debe verse como "activa".
     * @param {number} day - Valor del día (0–7)
     * @returns {boolean}
     */
    isDayActive(day) {
      if (day === 7) {
        return this.model.periodization.days.length === 7;
      }
      return this.model.periodization.days.includes(day);
    },
  },
};
</script>

<template lang="pug">
.modal__block
  .modal__section
    h3.section-title Periodización

    .pg

      //- Modo (Periódico / Días específicos) ───────────────────────────
      .pg-row
        .pg-row__label
          span
            span.req *
            | Modo
          .pg-row__hint Cómo se programa la repetición
        .pg-row__field
          .pg-radios
            el-radio(v-model="model.periodization.type" label="range") Periódico
            el-radio(v-model="model.periodization.type" label="explicit") Días específicos

      //- Modo "Periódico": cantidad de días + slider ────────────────────
      .pg-row(v-if="model.periodization.type === 'range'")
        .pg-row__label
          span
            span.req *
            | Cantidad de días
          .pg-row__hint Duración total del protocolo
        .pg-row__field
          .pg-range
            .pg-range__line
              span.pg-range__value {{ rangeDaysCount }}
              span.pg-range__unit días
              span.pg-range__hint(v-if="rangeDaysHint") — {{ rangeDaysHint }}
            el-slider(v-model="range" range :min="1" :max="365" :show-tooltip="false")
            .pg-range__bookends
              span Día 1
              span Día 365

      //- Modo "Periódico": días de la semana ────────────────────────────
      .pg-row(v-if="model.periodization.type === 'range'")
        .pg-row__label
          span
            span.req *
            | Días de la semana
        .pg-row__field
          .pg-days
            span.pg-day(
              v-for="day in weekdayOptions"
              :key="day.value"
              :class="{ 'is-on': isDayActive(day.value), 'pg-day--all': day.value === 7 }"
              @click="selectDay(day.value)"
            ) {{ day.name }}

      //- Modo "Días específicos": multi-select de días puntuales ────────
      .pg-row(v-else-if="model.periodization.type === 'explicit'")
        .pg-row__label
          span
            span.req *
            | Días del protocolo
          .pg-row__hint En qué días puntuales se dispara (1–365)
        .pg-row__field
          el-select.pg-select(
            v-model="model.periodization.days"
            placeholder="Agregar día (1–365)…"
            filterable multiple default-first-option
          )
            el-option(
              v-for="option in dayOptions"
              :key="option"
              :label="option"
              :value="option"
            )

      //- Horarios (siempre, en cualquier modo) ─────────────────────────
      .pg-row
        .pg-row__label
          span
            span.req *
            | Horarios
          .pg-row__hint Cuándo se dispara la tarea
        .pg-row__field
          el-select.pg-select(
            v-model="model.periodization.hours"
            placeholder="Agregar horario (HH:MM)…"
            filterable multiple default-first-option
          )
            el-option(
              v-for="option in timeOptions"
              :key="option"
              :label="option"
              :value="option"
            )

      //- Resumen ──────────────────────────────────────────────────────
      .pg-summary
        .pg-summary__title Resumen
        .pg-summary__body
          //- Bullet 1: rango de días (modo periódico) ó "Días específicos"
          .pg-summary__line(v-if="model.periodization.type === 'range'")
            span.pg-summary__error(
              v-if="!model.periodization.fromDay || !model.periodization.toDay"
            ) Falta seleccionar el rango de días
            span(v-else)
              | Desde el día
              b {{ model.periodization.fromDay }}
              |  al día
              b {{ model.periodization.toDay }}
              |  del protocolo
          //- Bullet 2: días de la semana ó días puntuales
          .pg-summary__line
            span.pg-summary__error(v-if="!model.periodization.days.length") Falta seleccionar los días
            span(
              v-else-if="model.periodization.type === 'range'"
              v-html="rangeDaysSummary"
            )
            span(
              v-else-if="model.periodization.type === 'explicit'"
              v-html="explicitDaysSummary"
            )
          //- Bullet 3: horarios
          .pg-summary__line
            span.pg-summary__error(v-if="!model.periodization.hours.length") Falta seleccionar los horarios
            span(v-else v-html="hoursSummary")
</template>

<style lang="scss" scoped>
$row-label-width: 200px;
$row-gap: 24px;
$border: #e8ebf2;
$border-soft: #f0f2f7;
$text-soft: #6b7592;
$text-mute: #a6adbf;

.pg {
  max-width: 760px;
}

/* ── Row layout: label-izquierda + field-derecha ── */
.pg-row {
  display: grid;
  grid-template-columns: $row-label-width 1fr;
  gap: $row-gap;
  align-items: start;
  padding: 12px 0;
}

.pg-row__label {
  font-size: 13px;
  font-weight: 500;
  color: $text-soft;
  padding-top: 8px;

  .req {
    color: #d44a3a;
    margin-right: 2px;
  }
}

.pg-row__hint {
  font-size: 12px;
  color: $text-mute;
  font-weight: 400;
  margin-top: 4px;
}

.pg-row__field {
  min-width: 0;
}

/* ── Radios horizontales ── */
.pg-radios {
  display: flex;
  gap: 24px;
  padding-top: 6px;

  ::v-deep .el-radio {
    margin-right: 0;
  }
}

/* ── Range (slider con valor numérico arriba + bookends) ── */
.pg-range {
  padding-top: 4px;
}

.pg-range__line {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 6px;
}

.pg-range__value {
  font-size: 20px;
  font-weight: 700;
  color: $primary;
  letter-spacing: -0.01em;
}

.pg-range__unit {
  color: $text-soft;
  font-size: 13px;
}

.pg-range__hint {
  color: $text-mute;
  font-size: 12px;
}

.pg-range__bookends {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: $text-mute;
  margin-top: 4px;
  padding: 0 6px;
}

/* Override Element UI slider styles for cleaner look */
.pg-range ::v-deep .el-slider {
  padding: 0 6px;
}
.pg-range ::v-deep .el-slider__runway {
  height: 4px;
  margin: 8px 0;
}
.pg-range ::v-deep .el-slider__bar {
  height: 4px;
  background-color: $primary;
}
.pg-range ::v-deep .el-slider__button {
  width: 14px;
  height: 14px;
  border: 2px solid $primary;
  background: white;
}
.pg-range ::v-deep .el-slider__button-wrapper {
  height: 30px;
  width: 30px;
  top: -13px;
}

/* ── Day pills (toggleables) ── */
.pg-days {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 6px;
}

.pg-day {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border: 1px solid $border;
  border-radius: 4px;
  font-size: 13px;
  color: $text-soft;
  cursor: pointer;
  background: white;
  user-select: none;
  transition: all 0.12s;

  &:hover {
    border-color: #c5cdd9;
    color: #1a2540;
  }

  &.is-on {
    background: $primary;
    color: white;
    border-color: $primary;
  }

  &--all.is-on {
    background: #28b495;
    border-color: #28b495;
  }
}

/* ── Multi-select (días puntuales / horarios) ── */
.pg-select {
  width: 100%;
  max-width: 480px;

  ::v-deep .el-select__tags .el-tag {
    background: rgba(44, 68, 152, 0.08);
    color: $primary;
    border-color: transparent;
    font-weight: 500;
  }
  ::v-deep .el-select__tags .el-tag .el-tag__close {
    color: $primary;
    opacity: 0.55;
    background: transparent;
  }
  ::v-deep .el-select__tags .el-tag .el-tag__close:hover {
    opacity: 1;
    background: transparent;
  }
}

/* ── Summary panel ── */
.pg-summary {
  margin-top: 24px;
  padding: 16px 0 0;
  border-top: 1px solid $border-soft;
}

.pg-summary__title {
  font-size: 12px;
  font-weight: 600;
  color: $text-mute;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 8px;
}

.pg-summary__body {
  font-size: 14px;
  color: #1a2540;
  line-height: 1.7;
}

.pg-summary__line {
  display: block;
}

.pg-summary__error {
  color: #d44a3a;
  font-style: italic;
  font-size: 13px;
}
</style>
